import { useCallback, useRef } from 'react';
import { useOpenWorkspacePanel } from '../panels/use-open-workspace-panel';
import { useWorkspaceStore } from '../panels/use-workspace-store';
import { SENSOR_GRID_PANEL, SensorGridWorkspacePanel } from './client-only-panel-types';

export type OpenSensorGridPanelArgs = {
    latitude: number;
    longitude: number;
};

export type OpenSensorGridPanel = (args: OpenSensorGridPanelArgs) => void;

/**
 * Opens the sensor grid for a point on the map.
 *
 * One grid panel at a time: opening it again re-points the open grid rather than stacking another
 * over the map. The opener focuses an existing panel of the type without applying the new config,
 * so the location is written to the open grid here first.
 */
export function useOpenSensorGridPanel(): OpenSensorGridPanel {
    const workspaceStore = useWorkspaceStore();
    // Stable identity: the map click handler takes this as an effect dependency and would
    // otherwise re-register its `mapView.on('click')` listener on every render.
    const openWorkspacePanel = useOpenWorkspacePanel();
    const openWorkspacePanelRef = useRef(openWorkspacePanel);
    openWorkspacePanelRef.current = openWorkspacePanel;

    return useCallback(
        ({ latitude, longitude }: OpenSensorGridPanelArgs) => {
            const openGrid = workspaceStore
                .getActivePanels()
                .find((panel) => panel.workspacePanelType === SENSOR_GRID_PANEL) as
                | SensorGridWorkspacePanel
                | undefined;
            openGrid?.updatePanelState('latitude', latitude);
            openGrid?.updatePanelState('longitude', longitude);

            openWorkspacePanelRef.current({
                workspacePanelConfig: {
                    workspacePanelType: SENSOR_GRID_PANEL,
                    // Cast contained here, as with the sensor event panel: the literal has no enum
                    // member for `panelState` to narrow against.
                    panelState: { latitude, longitude } as never,
                },
            });
        },
        [workspaceStore]
    );
}
